import React from "react";
import { Container, Row, Col, Card, Alert } from "react-bootstrap";
import { Link } from "react-router";
import {
  Activity,
  FileText,
  Tag,
  Package,
  CheckSquare,
  Shield,
  ArrowRight,
  GitPullRequest,
  Database,
} from "react-feather";
import { useAuth, usePermissions } from "../../hooks";
import { PageHeader } from "../../components/ui";
import {
  PermissionAction,
  PermissionResource,
  SystemGroup,
} from "../../interfaces";

interface IQuickLink {
  to: string;
  label: string;
  /** Short note on what the page is for, so the list is readable cold. */
  hint?: string;
}

interface ISection {
  title: string;
  description: string;
  icon: React.ReactNode;
  links: IQuickLink[];
  show: boolean;
}

export const Home: React.FC = () => {
  const { user } = useAuth();
  const { can, isAdmin, permissions } = usePermissions();

  const canAccessResource = (resource: PermissionResource): boolean =>
    isAdmin ||
    can(PermissionAction.ReadAny, resource) ||
    can(PermissionAction.CreateAny, resource) ||
    can(PermissionAction.UpdateAny, resource) ||
    can(PermissionAction.DeleteAny, resource);

  const canAccessKindredData = canAccessResource(
    PermissionResource.KindredData
  );
  const canAccessFullAutoCouponsTargeting = canAccessResource(
    PermissionResource.FullAutoCouponsTargeting
  );
  const canAccessAffiliateProducts = canAccessResource(
    PermissionResource.AffiliateProduct
  );
  const canAccessBasketTesting = canAccessResource(
    PermissionResource.BasketTesting
  );
  const canAccessWorkPipeline = canAccessResource(
    PermissionResource.WorkPipeline
  );
  const canAccessOpenData = canAccessResource(PermissionResource.OpenData);
  const canAccessOps =
    canAccessKindredData ||
    canAccessAffiliateProducts ||
    canAccessBasketTesting ||
    canAccessResource(PermissionResource.Task) ||
    canAccessResource(PermissionResource.Merchant);

  const canManageGroups =
    isAdmin ||
    can(PermissionAction.CreateAny, PermissionResource.GroupMembership) ||
    can(PermissionAction.DeleteAny, PermissionResource.GroupMembership);
  const canManageUsers =
    isAdmin || permissions?.groups.includes(SystemGroup.QaManagers);

  const sections: ISection[] = [
    {
      title: "Agentic",
      description:
        "Operational dashboard, merchants, tasks, and reporting for the agentic coupon workflows.",
      icon: <Activity size={28} className="text-primary" />,
      show: true,
      links: [
        {
          to: "/agentic/dashboard",
          label: "Dashboard",
          hint: "Runs, coupons and capture health",
        },
        ...(canAccessOps
          ? [
              {
                to: "/merchants",
                label: "Merchants",
                hint: "Every domain + geo we run",
              },
              {
                to: "/tasks",
                label: "Tasks",
                hint: "Individual runs and their logs",
              },
              {
                to: "/sessions",
                label: "Sessions",
                hint: "Captured signed-in sessions",
              },
            ]
          : []),
        {
          to: "/test-results",
          label: "Test Results",
          hint: "Outcome of each checkout test",
        },
        {
          to: "/coupon-results",
          label: "Coupon Results",
          hint: "Codes found, per merchant",
        },
        {
          to: "/auto-coupons/template-results",
          label: "Template Results",
          hint: "Sweep across templates and SKUs",
        },
      ],
    },
    {
      title: "Templates",
      description:
        "Auto-coupon templates, evaluations, execution runs, and agentic session monitoring.",
      icon: <FileText size={28} className="text-success" />,
      show: canAccessOps,
      links: [
        {
          to: "/auto-coupons/templates",
          label: "Templates",
          hint: "The per-platform run recipes",
        },
        {
          to: "/auto-coupons/evaluations",
          label: "Evaluations",
          hint: "How well each template scores",
        },
        {
          to: "/auto-coupons/runs",
          label: "Runs",
          hint: "Template execution history",
        },
        {
          to: "/auto-coupons/session-merchants",
          label: "Session Merchants",
          hint: "Merchants set up for session capture",
        },
        {
          to: "/auto-coupons/session-runs",
          label: "Session Runs",
          hint: "Capture runs and their outcomes",
        },
      ],
    },
    {
      title: "Kindred Live",
      description:
        "Manage live deals, coupons, offers, and brands powering the Kindred extension.",
      icon: <Tag size={28} className="text-danger" />,
      show: canAccessKindredData || canAccessFullAutoCouponsTargeting,
      links: [
        ...(canAccessFullAutoCouponsTargeting
          ? [
              {
                to: "/kindred/full-auto-coupons-targeting",
                label: "Full Auto Coupons Targeting",
                hint: "Who gets auto coupons, and where",
              },
            ]
          : []),
        ...(canAccessKindredData
          ? [
              { to: "/kindred/samsung", label: "Samsung", hint: "Samsung programme" },
              { to: "/kindred/deals", label: "Deals", hint: "Live deals" },
              { to: "/kindred/coupons", label: "Coupons", hint: "Live coupon codes" },
              { to: "/kindred/offers", label: "Offers", hint: "Live offers" },
              { to: "/kindred/brands", label: "Brands", hint: "Brand records" },
              {
                to: "/kindred/brand-domains",
                label: "Brand Domains",
                hint: "Domain-to-brand mapping",
              },
              {
                to: "/kindred/non-agentic",
                label: "Non-Agentic Merchants",
                hint: "Merchants we do not run agentically",
              },
              {
                to: "/kindred/qa",
                label: "QA Dashboard",
                hint: "QA throughput and backlog",
              },
            ]
          : []),
      ],
    },
    {
      title: "Work Pipeline",
      description:
        "The tester and developer queue: claim work, track stages, and report on throughput.",
      icon: <GitPullRequest size={28} className="text-primary" />,
      show: canAccessWorkPipeline,
      links: [
        {
          to: "/work-pipeline",
          label: "Pipeline",
          hint: "Claim and move items through stages",
        },
        {
          to: "/work-pipeline/productivity",
          label: "Productivity",
          hint: "Per-person throughput",
        },
        {
          to: "/work-pipeline/report",
          label: "Report",
          hint: "Pipeline state over time",
        },
        {
          to: "/work-pipeline/audit",
          label: "Audit",
          hint: "Who changed what, and when",
        },
      ],
    },
    {
      title: "Open Data",
      description:
        "Validation of the public merchant-map and product-map feeds we publish.",
      icon: <Database size={28} className="text-secondary" />,
      show: canAccessOpenData,
      links: [
        {
          to: "/open-data/validation",
          label: "Validation",
          hint: "Check feed entries before they ship",
        },
      ],
    },
    {
      title: "Affiliate Products",
      description:
        "Product sync configs, discovery, categories, and basket testing.",
      icon: <Package size={28} className="text-warning" />,
      show: canAccessAffiliateProducts || canAccessBasketTesting,
      links: [
        ...(canAccessAffiliateProducts
          ? [
              {
                to: "/affiliate-products/sync-configs",
                label: "Sync Configs",
                hint: "Per-network import settings",
              },
              {
                to: "/affiliate-products/discovery",
                label: "Discovery",
                hint: "Find advertisers worth syncing",
              },
              {
                to: "/affiliate-products/products",
                label: "Products",
                hint: "The synced product catalogue",
              },
              {
                to: "/affiliate-products/categories",
                label: "Categories",
                hint: "Category mapping",
              },
              {
                to: "/affiliate-products/merchant-review",
                label: "Merchant Review",
                hint: "Approve merchants for product data",
              },
              {
                to: "/affiliate-products/manual-entry",
                label: "Manual Entry",
                hint: "Add product data by hand",
              },
            ]
          : []),
        ...(canAccessBasketTesting
          ? [
              {
                to: "/affiliate-products/basket-tests",
                label: "Basket Testing",
                hint: "Test baskets end to end",
              },
            ]
          : []),
      ],
    },
    {
      title: "QA Lists",
      description: "Review and triage QA lists assigned to you.",
      icon: <CheckSquare size={28} className="text-info" />,
      show: true,
      links: [
        { to: "/qa-lists", label: "QA Lists", hint: "Your review queue" },
      ],
    },
    {
      title: "Admin",
      description:
        "Manage access control groups, permissions, and user display names.",
      icon: <Shield size={28} className="text-secondary" />,
      show: !!(canManageGroups || canManageUsers),
      links: [
        ...(canManageGroups
          ? [
              {
                to: "/admin/groups",
                label: "Groups & Permissions",
                hint: "Who can see what",
              },
            ]
          : []),
        ...(canManageUsers
          ? [{ to: "/admin/users", label: "Users", hint: "Display names and roles" }]
          : []),
      ],
    },
  ];

  const visibleSections = sections.filter((s) => s.show && s.links.length > 0);

  if (user?.role === "unassigned") {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <Alert.Heading>Account not yet assigned</Alert.Heading>
          <p className="mb-0">
            Your account has been created but has not been assigned a role yet.
            Please contact an admin to get access.
          </p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <PageHeader
        title={`Welcome${user ? `, ${user.username}` : ""}`}
        description="Pick an area below to jump into the backoffice."
      />
      <Row className="g-4">
        {visibleSections.map((section) => (
          <Col key={section.title} md={6} lg={4}>
            <Card className="h-100 shadow-sm">
              <Card.Body className="d-flex flex-column">
                <div className="d-flex align-items-center mb-2">
                  {section.icon}
                  <h5 className="mb-0 ms-2">{section.title}</h5>
                </div>
                <p className="text-muted small mb-3">{section.description}</p>
                <ul className="list-unstyled mb-0 mt-auto">
                  {section.links.map((link) => (
                    <li key={link.to} className="mb-2">
                      <Link
                        to={link.to}
                        className="d-flex align-items-start text-decoration-none"
                      >
                        <ArrowRight
                          size={14}
                          className="me-2 flex-shrink-0 mt-1"
                        />
                        <span>
                          {link.label}
                          {link.hint && (
                            <span className="d-block text-muted small">
                              {link.hint}
                            </span>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  );
};
